import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bookmark, Plus, Trash2, Inbox } from "lucide-react";

export default function LabelsPage() {
  const navigate = useNavigate();
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchLabels = useCallback(async () => {
    try {
      const { data } = await api.get("/labels");
      setLabels(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLabels(); }, [fetchLabels]);

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      await api.post("/labels", { name: trimmed });
      setNewName("");
      fetchLabels();
      toast.success(`Label "${trimmed}" created`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (deleteConfirm !== id) {
      setDeleteConfirm(id);
      setTimeout(() => setDeleteConfirm(null), 3000);
      return;
    }
    try {
      await api.delete(`/labels/${id}`);
      fetchLabels();
      toast.success("Label deleted");
    } catch {
      toast.error("Failed to delete");
    }
    setDeleteConfirm(null);
  };

  return (
    <div className="h-full flex flex-col page-enter" data-testid="labels-page">
      <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-[#E5E5E5] flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-medium text-[#111111]" data-testid="labels-title">Labels</h2>
          <p className="text-sm text-zinc-500 mt-0.5">Context: what is this email about?</p>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-4 sm:px-6 py-5 space-y-4">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Label list */}
            <div className="space-y-1">
              {labels.map((lbl) => (
                <div
                  key={lbl.label_id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F5F5F5] transition-colors group cursor-pointer"
                  onClick={() => navigate(`/inbox?label=${lbl.label_id}`)}
                  data-testid={`label-item-${lbl.label_id}`}
                >
                  <span className="text-sm text-[#555555] flex-1">{lbl.name}</span>
                  <Inbox size={14} className="text-zinc-300 group-hover:text-zinc-500 shrink-0" />
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(lbl.label_id); }}
                    className={`p-1 rounded shrink-0 transition-colors ${
                      deleteConfirm === lbl.label_id
                        ? "text-red-500 bg-red-50"
                        : "text-zinc-300 hover:text-red-600 opacity-0 group-hover:opacity-100"
                    }`}
                    data-testid={`label-delete-${lbl.label_id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>

            {/* Create new */}
            {labels.length < 10 && (
              <div className="flex items-center gap-2 pt-2">
                <Plus size={14} className="text-zinc-400 shrink-0" />
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                  placeholder="New label..."
                  className="h-9 text-sm"
                  data-testid="label-new-input"
                />
                {newName.trim() && (
                  <Button size="sm" onClick={handleCreate} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-3" data-testid="label-create-btn">
                    Add
                  </Button>
                )}
              </div>
            )}

            <p className="text-[10px] text-zinc-400 pt-2" data-testid="label-counter">
              {labels.length} / 10 labels used
            </p>
          </>
        )}
      </div>
    </div>
  );
}
